import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import {
  buildOptimisticQuotationPatch,
  formatQuotationMoney,
  getQuotationActionButtonClass,
  getQuotationBookingMetaClass,
  getQuotationContractMetaClass,
  hasRealBookingReference,
  OPTIMISTIC_BOOKING_REFERENCE,
  QUOTATION_STATUS_BADGE_CLASS,
  restoreQuotationStatusCache,
  updateQuotationStatusCache,
} from '@/lib/quotation-sales'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { quotationsApi } from '@/services/quotations'
import { salesApi } from '@/services/sales'
import type { BookingQuotationPayload } from '@/types'
import { ComplaintsPanel } from '@/components/jobs/job-complaints-panel'

const formatQuotationDetails = (value: unknown) => {
  if (!value) {
    return ''
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

type MetadataFormState = {
  enquiry: string
  source_followup: string
  contract_document_name: string
  contract_document_url: string
  quotation_details_input: string
  notes: string
  terms: string
}

const emptyMetadataForm = (): MetadataFormState => ({
  enquiry: 'none',
  source_followup: 'none',
  contract_document_name: '',
  contract_document_url: '',
  quotation_details_input: '',
  notes: '',
  terms: '',
})

const normalizeQuotationDetailsInput = (value: string): BookingQuotationPayload['quotation_details'] | undefined => {
  const raw = String(value || '').trim()
  if (!raw) {
    return undefined
  }
  try {
    return JSON.parse(raw) as BookingQuotationPayload['quotation_details']
  } catch {
    return { summary: raw }
  }
}

export default function CommerceQuotationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const [metadataForm, setMetadataForm] = useState<MetadataFormState>(emptyMetadataForm())
  const [contractFile, setContractFile] = useState<File | null>(null)
  const [activeQuotationAction, setActiveQuotationAction] = useState<'accept' | 'convert' | null>(null)

  const quotationQuery = useQuery({
    queryKey: ['commerce', 'quotation', id, serviceInstanceId],
    queryFn: () => quotationsApi.get(id!, serviceInstanceId),
    enabled: Boolean(id),
  })

  const enquiriesQuery = useQuery({
    queryKey: ['sales', 'enquiries', 'quotation-detail', serviceInstanceId],
    queryFn: () => salesApi.listEnquiries({ service_instance_id: serviceInstanceId ?? undefined }),
  })

  const followupsQuery = useQuery({
    queryKey: ['sales', 'followups', 'quotation-detail', serviceInstanceId],
    queryFn: () => salesApi.listFollowups({ service_instance_id: serviceInstanceId ?? undefined }),
  })

  const quotation = quotationQuery.data
  const enquiries = enquiriesQuery.data?.results ?? []
  const followups = followupsQuery.data?.results ?? []
  const availableFollowups = useMemo(() => {
    if (metadataForm.enquiry === 'none') {
      return followups
    }
    return followups.filter((item) => String(item.enquiry_id ?? '') === metadataForm.enquiry)
  }, [followups, metadataForm.enquiry])

  useEffect(() => {
    if (!quotation) {
      return
    }
    setMetadataForm({
      enquiry: quotation.enquiry ? String(quotation.enquiry) : 'none',
      source_followup: quotation.source_followup ? String(quotation.source_followup) : 'none',
      contract_document_name: quotation.contract_document_name || '',
      contract_document_url: quotation.contract_document_url || '',
      quotation_details_input: formatQuotationDetails(quotation.quotation_details),
      notes: quotation.notes || '',
      terms: quotation.terms || '',
    })
    setContractFile(null)
  }, [quotation])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['commerce', 'quotation', id, serviceInstanceId] })
    queryClient.invalidateQueries({ queryKey: ['commerce', 'quotations', serviceInstanceId] })
  }

  const quotationQueryKeys = [
    ['commerce', 'quotation', id, serviceInstanceId],
    ['commerce', 'quotations', serviceInstanceId],
    ['sales-dashboard', 'quotations', serviceInstanceId],
    ['sales', 'quotations-by-enquiry', serviceInstanceId],
    ['sales', 'followup-quotations', serviceInstanceId],
  ] as const

  const updateMetadataMutation = useMutation({
    mutationFn: () => quotationsApi.update(id!, {
      enquiry: metadataForm.enquiry === 'none' ? null : metadataForm.enquiry,
      source_followup: metadataForm.source_followup === 'none' ? null : metadataForm.source_followup,
      contract_document_name: metadataForm.contract_document_name || null,
      contract_document_url: metadataForm.contract_document_url || null,
      quotation_details: normalizeQuotationDetailsInput(metadataForm.quotation_details_input),
      notes: metadataForm.notes,
      terms: metadataForm.terms,
    }, serviceInstanceId),
    onSuccess: () => {
      toast.success('Quotation metadata updated')
      invalidate()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const uploadContractMutation = useMutation({
    mutationFn: () => {
      if (!contractFile) {
        throw new Error('Choose a contract file first.')
      }
      return quotationsApi.uploadContract(id!, {
        file: contractFile,
        contractDocumentName: metadataForm.contract_document_name || contractFile.name,
      }, serviceInstanceId)
    },
    onSuccess: () => {
      toast.success('Contract file uploaded')
      setContractFile(null)
      invalidate()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const removeContractMutation = useMutation({
    mutationFn: () => quotationsApi.removeContract(id!, serviceInstanceId),
    onSuccess: () => {
      toast.success('Contract removed')
      setContractFile(null)
      invalidate()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const acceptMutation = useMutation({
    mutationFn: () => quotationsApi.accept(id!, serviceInstanceId),
    onMutate: () => {
      setActiveQuotationAction('accept')
      return {
        snapshots: updateQuotationStatusCache(queryClient, id!, buildOptimisticQuotationPatch('accept'), quotationQueryKeys),
      }
    },
    onSuccess: () => {
      toast.success('Quotation accepted')
      invalidate()
    },
    onError: (error, _variables, context) => {
      restoreQuotationStatusCache(queryClient, context?.snapshots)
      toast.error(getErrorMessage(error))
    },
    onSettled: () => setActiveQuotationAction(null),
  })

  const convertMutation = useMutation({
    mutationFn: () => quotationsApi.convert(id!, serviceInstanceId),
    onMutate: () => {
      setActiveQuotationAction('convert')
      return {
        snapshots: updateQuotationStatusCache(queryClient, id!, buildOptimisticQuotationPatch('convert'), quotationQueryKeys),
      }
    },
    onSuccess: (result) => {
      updateQuotationStatusCache(queryClient, id!, {
        status: 'converted',
        converted_at: new Date().toISOString(),
        order_reference: result.order_reference ?? undefined,
        order_id: result.order_id ?? undefined,
        order: result.order_id ?? undefined,
      }, quotationQueryKeys)
      toast.success('Quotation converted to booking')
      if (result.order_reference) {
        navigate(withServiceInstanceId(`/services/orders/${result.order_reference}`, serviceInstanceId))
      } else {
        invalidate()
      }
    },
    onError: (error, _variables, context) => {
      restoreQuotationStatusCache(queryClient, context?.snapshots)
      toast.error(getErrorMessage(error))
    },
    onSettled: () => setActiveQuotationAction(null),
  })

  if (quotationQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading quotation...</div>
  }

  if (!quotation) {
    return <div className="text-sm text-muted-foreground">Quotation not found.</div>
  }

  const canAccept = quotation.status === 'draft' || quotation.status === 'sent'
  const canConvert = quotation.status === 'accepted'
  const canEdit = quotation.status === 'initiated'

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">{quotation.quotation_number}</h1>
            <Badge variant="outline" className={QUOTATION_STATUS_BADGE_CLASS[quotation.status] ?? 'border-slate-200 text-slate-700'}>{quotation.status}</Badge>
            {quotation.order_reference === OPTIMISTIC_BOOKING_REFERENCE ? (
              <Badge variant="secondary">Creating booking</Badge>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">Booking quotation detail and conversion.</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to={withServiceInstanceId('/services/quotations', serviceInstanceId)}>Back</Link>
          </Button>
          {canEdit ? (
            <Button asChild variant="secondary">
              <Link to={withServiceInstanceId(`/services/quotations/${quotation.id}/edit`, serviceInstanceId)}>Edit</Link>
            </Button>
          ) : null}
          {canAccept ? (
            <Button variant="outline" className={getQuotationActionButtonClass('accept')} disabled={acceptMutation.isPending} onClick={() => acceptMutation.mutate()}>
              {activeQuotationAction === 'accept' ? 'Accepting...' : 'Accept'}
            </Button>
          ) : null}
          {canConvert ? (
            <Button className={getQuotationActionButtonClass('convert')} disabled={convertMutation.isPending} onClick={() => convertMutation.mutate()}>
              {activeQuotationAction === 'convert' ? 'Converting...' : 'Convert to Booking'}
            </Button>
          ) : null}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{quotation.customer_name || 'Customer'}</CardTitle>
          <CardDescription>
            {[quotation.customer_email, quotation.customer_phone].filter(Boolean).join(' · ') || 'No contact details'}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div>
            <div className="text-xs uppercase text-muted-foreground">Linked order id</div>
            <div>{quotation.order_id || quotation.order || '-'}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Order reference</div>
            <div className={`mt-1 rounded-md border px-2.5 py-2 ${getQuotationBookingMetaClass(quotation.order_reference)}`}>
              {quotation.order_reference === OPTIMISTIC_BOOKING_REFERENCE ? (
                <Badge variant="secondary">Creating booking</Badge>
              ) : (
                quotation.order_reference || '-'
              )}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Sales enquiry</div>
            <div>{quotation.enquiry_name || '-'}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Service date</div>
            <div>{quotation.scheduled_date || '-'}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Time</div>
            <div>{[quotation.scheduled_start_time, quotation.scheduled_end_time].filter(Boolean).join(' - ') || '-'}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Valid until</div>
            <div>{quotation.valid_until || '-'}</div>
          </div>
          <div className="md:col-span-3">
            <div className="text-xs uppercase text-muted-foreground">Service address</div>
            <div>{quotation.service_address || '-'}</div>
          </div>
          <div className="md:col-span-3">
            <div className="text-xs uppercase text-muted-foreground">Follow-up</div>
            <div>{quotation.source_followup_title || '-'}</div>
          </div>
          <div className="md:col-span-3">
            <div className="text-xs uppercase text-muted-foreground">Contract document</div>
            <div className={`mt-1 rounded-md border px-2.5 py-2 ${getQuotationContractMetaClass(Boolean(quotation.effective_contract_document_url || quotation.contract_document_url))}`}>
              {quotation.effective_contract_document_url || quotation.contract_document_url ? (
                <a
                  className="font-medium text-primary underline-offset-4 hover:underline"
                  href={quotation.effective_contract_document_url || quotation.contract_document_url || '#'}
                  target="_blank"
                  rel="noreferrer"
                >
                  {quotation.contract_document_name || quotation.effective_contract_document_url || quotation.contract_document_url}
                </a>
              ) : (
                <div className="font-medium text-foreground">-</div>
              )}
            </div>
          </div>
          {quotation.quotation_details ? (
            <div className="space-y-2 md:col-span-3">
              <div className="text-xs uppercase text-muted-foreground">Quotation details</div>
              <Textarea value={formatQuotationDetails(quotation.quotation_details)} readOnly rows={8} />
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Edit Quotation Metadata</CardTitle>
          <CardDescription>Update sales links, contract references, and quotation notes without changing line items.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2 md:col-span-2">
            <Label>Sales enquiry</Label>
            <Select
              value={metadataForm.enquiry}
              onValueChange={(value) => setMetadataForm((current) => ({
                ...current,
                enquiry: value,
                source_followup: value === 'none' ? 'none' : current.source_followup,
              }))}
            >
              <SelectTrigger className="w-full"><SelectValue placeholder="Select enquiry" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No enquiry</SelectItem>
                {enquiries.map((enquiry) => (
                  <SelectItem key={enquiry.id} value={String(enquiry.id)}>
                    {[enquiry.name, enquiry.phone || enquiry.email].filter(Boolean).join(' · ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-1">
            <Label>Follow-up</Label>
            <Select value={metadataForm.source_followup} onValueChange={(value) => setMetadataForm((current) => ({ ...current, source_followup: value }))}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Select follow-up" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No follow-up</SelectItem>
                {availableFollowups.map((followup) => (
                  <SelectItem key={followup.id} value={String(followup.id)}>
                    {[followup.title, followup.status].filter(Boolean).join(' · ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Contract document name</Label>
            <Input value={metadataForm.contract_document_name} onChange={(event) => setMetadataForm((current) => ({ ...current, contract_document_name: event.target.value }))} />
          </div>
          <div className="space-y-2 md:col-span-1">
            <Label>Contract document link</Label>
            <Input value={metadataForm.contract_document_url} onChange={(event) => setMetadataForm((current) => ({ ...current, contract_document_url: event.target.value }))} placeholder="https://..." />
          </div>
          <div className="space-y-2 md:col-span-3">
            <Label>Notes</Label>
            <Textarea value={metadataForm.notes} onChange={(event) => setMetadataForm((current) => ({ ...current, notes: event.target.value }))} rows={3} />
          </div>
          <div className="space-y-2 md:col-span-3">
            <Label>Terms</Label>
            <Textarea value={metadataForm.terms} onChange={(event) => setMetadataForm((current) => ({ ...current, terms: event.target.value }))} rows={3} />
          </div>
          <div className="space-y-2 md:col-span-3">
            <Label>Quotation details</Label>
            <Textarea value={metadataForm.quotation_details_input} onChange={(event) => setMetadataForm((current) => ({ ...current, quotation_details_input: event.target.value }))} rows={7} />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button disabled={updateMetadataMutation.isPending} onClick={() => updateMetadataMutation.mutate()}>
              {updateMetadataMutation.isPending ? 'Saving...' : 'Save Metadata'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Complaint Conversation</CardTitle>
          <CardDescription>Manage customer email complaint threads linked to this quotation.</CardDescription>
        </CardHeader>
        <CardContent>
          <ComplaintsPanel quotationId={quotation.id} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Upload Contract File</CardTitle>
          <CardDescription>Attach the final contract document to this quotation.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2 md:col-span-2">
            <Label>Contract file</Label>
            <Input type="file" onChange={(event) => setContractFile(event.target.files?.[0] ?? null)} />
          </div>
          <div className="flex items-end gap-2 md:col-span-1">
            <Button className="flex-1" disabled={!contractFile || uploadContractMutation.isPending} onClick={() => uploadContractMutation.mutate()}>
              {uploadContractMutation.isPending ? 'Uploading...' : quotation?.contract_document_file_url ? 'Replace Contract' : 'Upload Contract'}
            </Button>
            <Button
              variant="outline"
              className={`flex-1 ${getQuotationActionButtonClass('contract')}`}
              disabled={!(quotation?.contract_document_file_url || quotation?.contract_document_url) || removeContractMutation.isPending}
              onClick={() => removeContractMutation.mutate()}
            >
              {removeContractMutation.isPending ? 'Removing...' : 'Remove'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quoted Booking Items</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(quotation.order_items_data ?? []).map((line) => (
                <TableRow key={String(line.id)}>
                  <TableCell>{line.item_type ?? 'item'}</TableCell>
                  <TableCell>
                    <div className="font-medium">{line.product_variant_label || line.product_name || `Item #${line.id}`}</div>
                    {line.notes ? <div className="text-xs text-muted-foreground">{line.notes}</div> : null}
                    {(line.addons ?? []).length > 0 ? (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Add-ons: {(line.addons ?? []).map((addon) => `${addon.addon_name || `#${addon.addon}`} x ${addon.qty ?? 1}`).join(', ')}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>{line.qty}</TableCell>
                  <TableCell>{formatQuotationMoney(line.unit_price)}</TableCell>
                  <TableCell>{formatQuotationMoney(line.final_price)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {(quotation.inventory_lines ?? []).length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Additional Inventory</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Inventory</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(quotation.inventory_lines ?? []).map((line) => (
                  <TableRow key={String(line.id)}>
                    <TableCell>
                      <div className="font-medium">{line.title}</div>
                      {line.description ? <div className="text-xs text-muted-foreground">{line.description}</div> : null}
                    </TableCell>
                    <TableCell>{line.quantity}</TableCell>
                    <TableCell>{formatQuotationMoney(line.unit_price)}</TableCell>
                    <TableCell>{formatQuotationMoney(line.line_total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Totals</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(quotation.total_snapshot?.total_rows ?? [
            { label: 'Sub Total', amount: quotation.subtotal },
            { label: 'Tax', amount: quotation.tax_amount },
            { label: 'Total', amount: quotation.total },
          ]).map((row, index) => (
            <div key={index} className="flex items-center justify-between border-b py-2 last:border-b-0">
              <span className={row.label === 'Total' ? 'font-semibold' : 'text-muted-foreground'}>{row.label}</span>
              <span className={row.label === 'Total' ? 'font-semibold' : ''}>{formatQuotationMoney(row.amount)}</span>
            </div>
          ))}
          {quotation.order_reference ? (
            <div className={`pt-3 rounded-md border px-2.5 py-2 text-sm ${getQuotationBookingMetaClass(quotation.order_reference)}`}>
              <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Booking</div>
              <div className="pt-1 text-muted-foreground">
                {hasRealBookingReference(quotation.order_reference) ? quotation.order_reference : <Badge variant="secondary">Creating booking</Badge>}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
